"use client";

import * as React from "react";

type Node = { x: number; y: number; r: number; threat: 0 | 1 | 2; seed: number };

/**
 * Live attack-surface visual — a radar sweep rotating over a drifting node
 * constellation, links between near nodes, threat nodes flaring when swept.
 * Plain canvas + rAF (no three.js). Respects reduced motion.
 */
export function AttackSurface({ className }: { className?: string }) {
  const ref = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let W = 0, H = 0, cx = 0, cy = 0, R = 0;
    let net = "255, 255, 255"; // neutral ring/axis tint — theme-aware via --lp-net-rgb
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    // deterministic pseudo-random so layout is stable
    let s = 1337;
    const rnd = () => { s = (s * 1664525 + 1013904223) % 0xffffffff; return s / 0xffffffff; };

    let nodes: Node[] = [];
    const build = () => {
      const rect = canvas.getBoundingClientRect();
      net = getComputedStyle(canvas).getPropertyValue("--lp-net-rgb").trim() || "255, 255, 255";
      W = rect.width; H = rect.height;
      canvas.width = W * DPR; canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      cx = W * 0.5; cy = H * 0.52; R = Math.min(W, H) * 0.46;
      s = 1337;
      nodes = Array.from({ length: 46 }, () => {
        const a = rnd() * Math.PI * 2;
        const rr = Math.sqrt(rnd()) * R * 0.96;
        const threat = rnd() > 0.86 ? (rnd() > 0.5 ? 2 : 1) : 0;
        return { x: cx + Math.cos(a) * rr, y: cy + Math.sin(a) * rr, r: 1.3 + rnd() * 1.8, threat: threat as 0 | 1 | 2, seed: rnd() * 1000 };
      });
    };
    build();

    const accent = "#6d7bff", violet = "#a78bfa", red = "#ff5d6c", amber = "#f0a868";
    let raf = 0; const t0 = performance.now();

    const draw = (now: number) => {
      const t = (now - t0) / 1000;
      const sweep = reduce ? Math.PI * 1.5 : (t * 0.5) % (Math.PI * 2);
      ctx.clearRect(0, 0, W, H);

      // rings
      ctx.lineWidth = 1;
      for (let i = 1; i <= 4; i++) {
        ctx.beginPath();
        ctx.arc(cx, cy, (R * i) / 4, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${net},${0.07 - i * 0.008})`;
        ctx.stroke();
      }
      // cross axes
      ctx.strokeStyle = `rgba(${net},0.05)`;
      ctx.beginPath(); ctx.moveTo(cx - R, cy); ctx.lineTo(cx + R, cy); ctx.moveTo(cx, cy - R); ctx.lineTo(cx, cy + R); ctx.stroke();

      // links between near nodes
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const d = Math.hypot(dx, dy);
          if (d < R * 0.34) {
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(109,123,255,${0.12 * (1 - d / (R * 0.34))})`;
            ctx.stroke();
          }
        }
      }

      // sweep wedge
      if (!reduce) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(sweep);
        const g = ctx.createLinearGradient(0, 0, R, 0);
        g.addColorStop(0, "rgba(109,123,255,0.22)");
        g.addColorStop(1, "rgba(109,123,255,0)");
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, R, -0.35, 0); ctx.closePath();
        ctx.fillStyle = g; ctx.fill();
        ctx.strokeStyle = "rgba(167,139,250,0.5)"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(R, 0); ctx.stroke();
        ctx.restore();
      }

      // nodes
      for (const n of nodes) {
        const ang = Math.atan2(n.y - cy, n.x - cx);
        let rel = (ang - sweep) % (Math.PI * 2); if (rel < 0) rel += Math.PI * 2;
        const lit = reduce ? 0.6 : Math.max(0, 1 - rel * 1.6); // recently swept → bright
        const base = n.threat === 2 ? red : n.threat === 1 ? amber : accent;
        const pulse = 0.55 + 0.45 * Math.sin(t * 2 + n.seed);
        const a = n.threat ? 0.5 + 0.5 * pulse : 0.35 + 0.5 * lit;
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r + (n.threat ? 0.8 : 0) + lit * 1.4, 0, Math.PI * 2);
        ctx.fillStyle = hexA(base, a); ctx.fill();
        if (n.threat && (lit > 0.2 || reduce)) {
          ctx.beginPath(); ctx.arc(n.x, n.y, n.r + 6 + pulse * 5, 0, Math.PI * 2);
          ctx.strokeStyle = hexA(base, 0.25 * (reduce ? 1 : lit)); ctx.lineWidth = 1; ctx.stroke();
        }
      }

      // center
      ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2); ctx.fillStyle = violet; ctx.fill();

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    const onResize = () => build();
    window.addEventListener("resize", onResize);
    // rebuild (re-reads the tint) when the landing theme toggles
    const mo = new MutationObserver(() => build());
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-lp-theme", "class"] });
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); mo.disconnect(); };
  }, []);

  return <canvas ref={ref} className={className} aria-hidden />;
}

function hexA(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${Math.max(0, Math.min(1, a))})`;
}
